import { type RefObject, useCallback, useEffect, useRef } from 'react';

const STICK_TO_BOTTOM_THRESHOLD_PX = 32;

type UseTranscriptScrollOptions = {
    readonly currentRunNodeCount?: number;
    readonly renderedTimelineEntryCount: number;
    readonly streamOverlayAssistantMessageCount?: number;
    readonly isAgentBusy: boolean;
};

type UseTranscriptScrollReturn = {
    readonly transcriptContentRef: RefObject<HTMLDivElement | null>;
    readonly transcriptScrollAreaRef: RefObject<HTMLDivElement | null>;
    readonly stickTranscriptToBottomRef: RefObject<boolean>;
    readonly scrollTranscriptToBottom: (behavior?: ScrollBehavior) => void;
    readonly handleTypewriterProgress: () => void;
};

export function useTranscriptScroll({
    currentRunNodeCount = 0,
    renderedTimelineEntryCount,
    streamOverlayAssistantMessageCount = 0,
    isAgentBusy,
}: UseTranscriptScrollOptions): UseTranscriptScrollReturn {
    const transcriptContentRef = useRef<HTMLDivElement | null>(null);
    const transcriptScrollAreaRef = useRef<HTMLDivElement | null>(null);
    const stickTranscriptToBottomRef = useRef(true);
    const getTranscriptViewport = useCallback(
        () =>
            transcriptScrollAreaRef.current?.querySelector<HTMLElement>(
                '[data-slot="scroll-area-viewport"]',
            ) ?? null,
        [],
    );
    const scrollTranscriptToBottom = useCallback(
        (behavior: ScrollBehavior = 'auto') => {
            const viewport = getTranscriptViewport();
            if (!viewport) {
                return;
            }

            const top = viewport.scrollHeight;
            if (typeof viewport.scrollTo === 'function') {
                viewport.scrollTo({ top, behavior });
            } else {
                viewport.scrollTop = top;
            }
        },
        [getTranscriptViewport],
    );
    const handleTypewriterProgress = useCallback(() => {
        if (stickTranscriptToBottomRef.current) {
            scrollTranscriptToBottom();
        }
    }, [scrollTranscriptToBottom]);
    useEffect(() => {
        const viewport = getTranscriptViewport();
        if (!viewport) {
            return;
        }

        const handleScroll = () => {
            const distanceToBottomPx =
                viewport.scrollHeight -
                viewport.scrollTop -
                viewport.clientHeight;
            stickTranscriptToBottomRef.current =
                distanceToBottomPx <= STICK_TO_BOTTOM_THRESHOLD_PX;
        };
        const handleWheel = (event: WheelEvent) => {
            if (event.deltaY < 0) {
                stickTranscriptToBottomRef.current = false;
            }
        };

        viewport.addEventListener('scroll', handleScroll, { passive: true });
        viewport.addEventListener('wheel', handleWheel, { passive: true });
        return () => {
            viewport.removeEventListener('scroll', handleScroll);
            viewport.removeEventListener('wheel', handleWheel);
        };
    }, [
        currentRunNodeCount,
        getTranscriptViewport,
        renderedTimelineEntryCount,
        streamOverlayAssistantMessageCount,
    ]);
    useEffect(() => {
        if (stickTranscriptToBottomRef.current) {
            scrollTranscriptToBottom();
        }
    }, [
        currentRunNodeCount,
        renderedTimelineEntryCount,
        scrollTranscriptToBottom,
        streamOverlayAssistantMessageCount,
    ]);
    useEffect(() => {
        const contentElement = transcriptContentRef.current;
        if (
            !isAgentBusy ||
            !contentElement ||
            typeof ResizeObserver === 'undefined'
        ) {
            return;
        }

        let animationFrameId: number | null = null;
        const scrollAfterResize = () => {
            if (!stickTranscriptToBottomRef.current) {
                return;
            }
            if (animationFrameId !== null) {
                window.cancelAnimationFrame(animationFrameId);
            }
            animationFrameId = window.requestAnimationFrame(() => {
                animationFrameId = null;
                scrollTranscriptToBottom();
            });
        };
        const resizeObserver = new ResizeObserver(scrollAfterResize);
        resizeObserver.observe(contentElement);
        scrollAfterResize();

        return () => {
            if (animationFrameId !== null) {
                window.cancelAnimationFrame(animationFrameId);
            }
            resizeObserver.disconnect();
        };
    }, [isAgentBusy, scrollTranscriptToBottom]);

    return {
        transcriptContentRef,
        transcriptScrollAreaRef,
        stickTranscriptToBottomRef,
        scrollTranscriptToBottom,
        handleTypewriterProgress,
    };
}
